import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
import { Bloom } from './Bloom';
import { CTAButton } from './CTAButton';

/* 2026-09-17 (Kevin: "Go, I want the website completely optimized and built
   to generate inbounds in local search"). Shared blocks for the five money
   pages from the 2026-08-13 keyword realignment plus the rewritten Google
   Business Profile setup page. Same visual language as WhatItFixes (dark
   surfaces, emerald accent, monospace labels, whileInView reveals). Nothing
   here mentions reviews or reputation, and no page that uses these blocks
   may add it. Pricing on every page is The Viridia System at $897 a month,
   with no setup fee (Kevin, 2026-09-17: "The Viridia Package is $897 no
   setup fee"). Never add a setup or onboarding charge to these blocks. */

export const BASE_URL = 'https://viridiaanalytics.com';
export const ORG_ID = `${BASE_URL}/#organization`;
export const METRO = ['Phoenix', 'Scottsdale', 'Glendale', 'Peoria', 'Chandler', 'Mesa', 'Gilbert', 'Tempe', 'Queen Creek', 'Buckeye', 'Surprise', 'Goodyear'];
export const metroAreaServed = METRO.map(name => ({
  '@type': 'City',
  name
}));
export type Faq = {
  question: string;
  answer: string;
};
export function serviceJsonLd(opts: {
  name: string;
  serviceType: string;
  description: string;
  path: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${BASE_URL}${opts.path}#service`,
    name: opts.name,
    serviceType: opts.serviceType,
    description: opts.description,
    url: `${BASE_URL}${opts.path}`,
    provider: {
      '@id': ORG_ID
    },
    areaServed: metroAreaServed,
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Home service businesses and contractors'
    },
    offers: {
      '@type': 'Offer',
      name: 'The Viridia System',
      url: `${BASE_URL}/pricing#viridia-system`,
      priceCurrency: 'USD',
      price: '897',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '897',
        priceCurrency: 'USD',
        unitText: 'MONTH'
      }
    }
  };
}
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.4
  }
};
type SplitProps = {
  label: string;
  title: string;
  body: React.ReactNode;
  visual: React.ReactNode;
  flip?: boolean;
  alt?: boolean;
  note?: string;
  children?: React.ReactNode;
};
export function SplitSection({
  label,
  title,
  body,
  visual,
  flip,
  alt,
  note,
  children
}: SplitProps) {
  return <section className={`relative isolate overflow-hidden border-b border-[#1a1a1a] ${alt ? 'bg-[#0c0c0c]' : ''}`} data-id="element-390" data-mp="2menk0jfbwvg8">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-24" data-id="element-391" data-mp="3e865zo3xhnnu">
        <motion.div {...reveal} className={flip ? 'lg:order-2' : ''} data-id="element-392" data-mp="10q1slvtk0rul">
          <SectionLabel data-id="element-393" data-mp="15ocwzr12ya5f">{label}</SectionLabel>
          <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-394" data-mp="15p608i0tyhit">
            {title}
          </h2>
          <div className="mt-5 max-w-md space-y-4 text-lg leading-relaxed text-neutral-400" data-id="element-395" data-mp="19sayg8dvnx1n">{body}</div>
          {note && <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-emerald-500" data-id="element-396" data-mp="1jonrd41lm6vc">{note}</p>}
          {children && <div className="mt-8" data-id="element-397" data-mp="1olxpd831zqus">{children}</div>}
        </motion.div>
        <motion.div {...reveal} transition={{
        duration: 0.4,
        delay: 0.08
      }} className={flip ? 'lg:order-1' : ''} data-id="element-398" data-mp="1tdfft27ga91i">
          {visual}
        </motion.div>
      </div>
    </section>;
}
type ChecklistProps = {
  label: string;
  title: string;
  body?: React.ReactNode;
  items: {
    title: string;
    body: string;
  }[];
  alt?: boolean;
  columns?: 2 | 3;
};
export function ChecklistSection({
  label,
  title,
  body,
  items,
  alt,
  columns = 2
}: ChecklistProps) {
  return <section className={`relative isolate overflow-hidden border-b border-[#1a1a1a] ${alt ? 'bg-[#0c0c0c]' : ''}`} data-id="element-399" data-mp="1os6j9waoajje">
      <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-400" data-mp="23frllnzq4eq9" />
      <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-401" data-mp="1yhpz0t339xa1">
        <SectionLabel data-id="element-402" data-mp="1yihum3worv0b">{label}</SectionLabel>
        <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-403" data-mp="1arlwkp1n03km">
          {title}
        </h2>
        {body && <div className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-404" data-mp="15uvig79e7i1u">{body}</div>}
        <ul className={`mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] ${columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`} data-id="element-405" data-mp="1krw8m6bxr4y7">
          {items.map((f, i) => <motion.li key={f.title} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className={`p-8 ${alt ? 'bg-[#0d0d0d]' : 'bg-[#0a0a0a]'}`} data-id="element-406" data-mp="2r6wzhxm3swxx" data-mpi={i}>
              <div className="flex items-start gap-3" data-id="element-407" data-mp="2pr1caq2lzcdg">
                <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-408" data-mp="2tq1r7xhytuv6" />
                <div data-id="element-409" data-mp="3hg4f01eukenz">
                  <h3 className="text-base font-semibold text-white" data-id="element-410" data-mp="3hgxovb93rkac">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-411" data-mp="3mefpmecyut79">{f.body}</p>
                </div>
              </div>
            </motion.li>)}
        </ul>
      </div>
    </section>;
}
type CompareRow = {
  criterion: string;
  cells: (string | boolean)[];
};
export function CompareTable({
  columns,
  rows,
  highlight
}: {
  columns: string[];
  rows: CompareRow[];
  highlight?: number;
}) {
  return <div className="overflow-x-auto rounded-2xl border border-[#1c1c1c]" data-id="element-412" data-mp="91vs99z0ai3q">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm" data-id="element-413" data-mp="ivjknpzam013">
        <thead data-id="element-414" data-mp="eug1ouly7nvn">
          <tr className="bg-[#0d0d0d]" data-id="element-415" data-mp="ew10iwfapszb">
            <th scope="col" className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500" data-id="element-416" data-mp="esvczlmbez5p">
              What matters
            </th>
            {columns.map((c, i) => <th key={c} scope="col" className={`px-5 py-4 font-mono text-[11px] uppercase tracking-[0.15em] ${i === highlight ? 'text-emerald-500' : 'text-neutral-500'}`} data-id="element-417" data-mp="4z61mcf41qki" data-mpi={i}>
                {c}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-418" data-mp="3icr99j29ik4p">
          {rows.map(r => <tr key={r.criterion} className="border-t border-[#1c1c1c]" data-id="element-419" data-mp="3i9vc3wy8nzhb">
              <th scope="row" className="px-5 py-4 font-medium text-neutral-200" data-id="element-420" data-mp="3dbttfd4q626d">
                {r.criterion}
              </th>
              {r.cells.map((cell, i) => <td key={i} className={`px-5 py-4 leading-relaxed ${i === highlight ? 'bg-emerald-500/[0.05] text-neutral-200' : 'text-neutral-400'}`} data-id="element-421" data-mp="2y8k20wvir451" data-mpi={i}>
                  {cell === true ? <span className="inline-flex items-center gap-1.5 text-emerald-500" data-id="element-422" data-mp="3d749mpvgf84r">
                      <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-423" data-mp="2fjm19g5pg81a" /> Yes
                    </span> : cell === false ? <span className="text-neutral-600" data-id="element-424" data-mp="2kigjafvulskc">No</span> : cell}
                </td>)}
            </tr>)}
        </tbody>
      </table>
    </div>;
}
export function ProofStrip({
  items
}: {
  items: {
    value: string;
    label: string;
    to: string;
    source: string;
  }[];
}) {
  return <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-425" data-mp="21qnqomkoy9d5">
      <div className="mx-auto w-full max-w-6xl px-6 py-16 lg:py-20" data-id="element-426" data-mp="3fz75ilc69505">
        <SectionLabel data-id="element-427" data-mp="3bwtyltlov11s">From Phoenix clients</SectionLabel>
        <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-428" data-mp="3bydgfomztk5o">
          {items.map((p, i) => <motion.li key={p.label} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.4,
          delay: i * 0.06
        }} data-id="element-429" data-mp="3pxmy6squkmbr" data-mpi={i}>
              <Link to={p.to} className="group flex h-full flex-col rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] p-6 transition-colors hover:border-emerald-500/60" data-id="element-430" data-mp="3uun0cskkt4xl">
                <span className="text-3xl font-bold tracking-tight text-white" data-id="element-431" data-mp="7j46t531eqny">{p.value}</span>
                <span className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-432" data-mp="mfuystadt03j">{p.label}</span>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-500" data-id="element-433" data-mp="hi34jsm9l1w6">
                  {p.source}
                  <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-434" data-mp="1546rsm5atbjd" />
                </span>
              </Link>
            </motion.li>)}
        </ul>
      </div>
    </section>;
}
export function FaqSection({
  title,
  faqs,
  alt
}: {
  title: string;
  faqs: Faq[];
  alt?: boolean;
}) {
  return <section className={`border-b border-[#1a1a1a] ${alt ? 'bg-[#0c0c0c]' : ''}`} data-id="element-435" data-mp="hchv6tlg20wm">
      <div className="mx-auto w-full max-w-3xl px-6 py-20 lg:py-24" data-id="element-436" data-mp="m75mt1bqvbff">
        <SectionLabel data-id="element-437" data-mp="8exsdg7xnwjw">Questions</SectionLabel>
        <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-438" data-mp="dcqy685bqojn">
          {title}
        </h2>
        <div className="mt-12 space-y-8 border-t border-[#1c1c1c] pt-10" data-id="element-439" data-mp="3ulw1web4n22b">
          {faqs.map(item => <div key={item.question} data-id="element-440" data-mp="3d8hc0kpnivt">
              <h3 className="text-lg font-semibold text-white" data-id="element-441" data-mp="3ge77i15yosl">{item.question}</h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-442" data-mp="2m4fu8sd7lylo">{item.answer}</p>
            </div>)}
        </div>
      </div>
    </section>;
}
export function PriceSection({
  title,
  body,
  alt
}: {
  title: string;
  body: React.ReactNode;
  alt?: boolean;
}) {
  return <section className={`relative isolate overflow-hidden border-b border-[#1a1a1a] ${alt ? 'bg-[#0c0c0c]' : ''}`} data-id="element-443" data-mp="2h6wflrclnjt7">
      <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-444" data-mp="34yl2utakdi3a" />
      <div className="mx-auto w-full max-w-3xl px-6 py-20 text-center lg:py-24" data-id="element-445" data-mp="39ujx2xikpubm">
        <div className="flex justify-center" data-id="element-446" data-mp="2v5nql4w60pd4">
          <SectionLabel data-id="element-447" data-mp="305ii9anrliz8">What it costs</SectionLabel>
        </div>
        <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-448" data-mp="303xo3m5d421w">
          {title}
        </h2>
        <div className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-449" data-mp="3slt7d9064n49">{body}</div>
        <p className="mt-8 flex items-baseline justify-center gap-2" data-id="element-450" data-mp="1h6ucofuviwg4">
          <span className="text-5xl font-bold tracking-tight text-white" data-id="element-451" data-mp="1h4zrr1jlr4c1">$897</span>
          <span className="text-base text-neutral-500" data-id="element-452" data-mp="1c6y566mywmvt">a month, month to month</span>
        </p>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-emerald-500" data-id="element-453" data-mp="1qvcaco7uwnfj">
          No setup fee &middot; No contract
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4" data-id="element-454" data-mp="y7yl7caegw5z">
          <CTAButton to="/pricing#viridia-system" data-id="element-455" data-mp="ya2njpsnhn45">See what is included</CTAButton>
          <CTAButton to="/contact" variant="ghost" data-id="element-456" data-mp="tagbhjok6cwx">
            Book a 15-min call
          </CTAButton>
        </div>
      </div>
    </section>;
}
export function RelatedLinks({
  title,
  links
}: {
  title: string;
  links: {
    to: string;
    label: string;
    body: string;
  }[];
}) {
  return <section className="border-b border-[#1a1a1a]" data-id="element-457" data-mp="2im8lnke9qk0p">
      <div className="mx-auto w-full max-w-6xl px-6 py-16 lg:py-20" data-id="element-458" data-mp="2emhydqnyam0x">
        <SectionLabel data-id="element-459" data-mp="2eoauwd4unpv7">Keep reading</SectionLabel>
        <h2 className="mt-6 text-2xl font-bold tracking-tight text-white sm:text-3xl" data-id="element-460" data-mp="1bc3s67move1s">{title}</h2>
        <nav aria-label="Related pages" className="mt-10" data-id="element-461" data-mp="1g9m486w069e0">
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-462" data-mp="1gc8eieaa32zx">
            {links.map(r => <li key={r.to} data-id="element-463" data-mp="1patrltwzdptb">
                <Link to={r.to} className="group flex h-full flex-col rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] p-6 transition-colors hover:border-emerald-500/60" data-id="element-464" data-mp="1pa25rat6r6g8">
                  <span className="flex items-center gap-2 text-base font-semibold text-white" data-id="element-465" data-mp="xkfmam319lt9">
                    {r.label}
                    <ArrowRightIcon className="h-4 w-4 text-emerald-500 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-466" data-mp="11jobddn4ovih" />
                  </span>
                  <span className="mt-2.5 text-sm leading-relaxed text-neutral-400" data-id="element-467" data-mp="9ttlnfvcz3ta">{r.body}</span>
                </Link>
              </li>)}
          </ul>
        </nav>
      </div>
    </section>;
}

/* Name, phone, email and city, in one visible block, matching the footer,
   the Google Business Profile and the JSON-LD exactly. Only the Phoenix
   location page uses it. */
export function NapBlock() {
  return <div className="rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7" data-id="element-468" data-mp="3tiy7mf01sl63">
      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500" data-id="element-469" data-mp="3ohqzufxwnlzf">Viridia Analytics LLC</p>
      <ul className="mt-5 space-y-3.5 text-sm text-neutral-300" data-id="element-470" data-mp="3jl0mi0dhj4hp">
        <li className="flex items-center gap-2.5" data-id="element-471" data-mp="3erfj1st4hib4">
          <PhoneIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-472" data-mp="39t4b5nme1hvj" />
          <a href="tel:6233000708" className="hover:text-white" data-id="element-473" data-mp="39qrtzcogfbjj">
            (623) 300-0708
          </a>
        </li>
        <li className="flex items-center gap-2.5" data-id="element-474" data-mp="30s6ghvxuampm">
          <MailIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-475" data-mp="30rwpc51269ic" />
          <a href="mailto:info@viridiaanalytics.com" className="hover:text-white" data-id="element-476" data-mp="2vuo4fwoizrde">
            info@viridiaanalytics.com
          </a>
        </li>
        <li className="flex items-start gap-2.5" data-id="element-477" data-mp="14pbgm8bfalne">
          <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-478" data-mp="14o02bekcpvi1" />
          <span data-id="element-479" data-mp="pzm1182l2f3l">Phoenix, AZ 85050. Serving the whole Valley.</span>
        </li>
      </ul>
      <p className="mt-6 border-t border-[#1c1c1c] pt-5 text-xs leading-relaxed text-neutral-500" data-id="element-480" data-mp="uyghi3d2vrkj">
        Hours: Monday to Friday, 8am to 5pm Arizona time. Your callers get answered around the clock.
      </p>
      <div className="mt-6" data-id="element-481" data-mp="l23q5c509psy">
        <CTAButton to="/contact" data-id="element-482" data-mp="kz7pvh5oqp1c">Book a 15-min call</CTAButton>
      </div>
    </div>;
}